import type { TeamId, WeaponId } from '@rekrut/shared';

/** Боец — один зритель на поле. */
export interface Unit {
  userId: string;
  nickname: string;
  avatarUrl: string;
  team: TeamId;

  x: number;
  y: number;
  vx: number;
  vy: number;

  hp: number;
  maxHp: number;
  radius: number;

  /** 1..maxLevel. Растёт за добивающий выстрел, при смерти сбрасывается. */
  level: number;
  kills: number;

  invulnUntil: number;
  shieldUntil: number;
  crownUntil: number;
  levelUpAt: number;

  nextShotAt: number;
  nextTargetAt: number;
  targetId: string | null;
  /** Целью выбран трон противника, а не боец. */
  targetIsThrone: boolean;
  driftPhase: number;

  /** Момент залпа — по нему рендер раздувает и заливает шар красным. */
  castAt: number;
  castDirX: number;
  castDirY: number;
  /**
   * Импульс отдачи, px/с. Настоящая скорость в симуляции, а не смещение
   * картинки: шар физически отлетает назад и потом идёт обратно вперёд.
   */
  kbX: number;
  kbY: number;

  /** Серия: когда был прошлый кил и сколько их подряд в коротком окне. */
  lastKillAt: number;
  combo: number;

  spawnedAt: number;
  lastDamagedAt: number;
  hitFlash: number;
  showNameUntil: number;
}

export interface Projectile {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  team: TeamId;
  damage: number;
  ownerId: string;
  diesAt: number;
  /** Ракета от подарка: взрывается по площади. */
  blastRadius: number;
  throneDamage: number;
  /** Свой цвет снаряда; 0 — берём цвет команды. */
  tint: number;
  /** Точка прицеливания: место бойца на момент пуска или трон. */
  tx: number;
  ty: number;
  /**
   * Амплитуда спирали, рад. Ракета не наводится, а идёт к точке пуска
   * закручиваясь: угол к цели гуляет на ±spiral, затухая у самой цели.
   */
  spiral: number;
  spinPhase: number;
  spinRate: number;
}

/** Ждущий в очереди. Аватарки первых видно на экране. */
export interface QueueEntry {
  userId: string;
  nickname: string;
  avatarUrl: string;
  team: TeamId;
  queuedAt: number;
  /** Раньше этого времени в бой не выпускаем — пауза после смерти. */
  readyAt: number;
  /** Прошёл без очереди за донат. */
  priority: boolean;
  /** То же самое для того, кто стреляет, ещё стоя в очереди. */
  castAt: number;
  castDirX: number;
  castDirY: number;
}

export interface Throne {
  team: TeamId;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  /** Щит держат живые защитники вне своей зоны. */
  shielded: boolean;
  defenders: number;
  /** Когда началась текущая осада, 0 — трон под щитом. */
  siegeSince: number;
  hitFlash: number;
  lastDamagedAt: number;
}

export interface TeamState {
  id: TeamId;
  label: string;
  color: number;
  colorDark: number;
  queue: QueueEntry[];
  /** userId → диаманты за матч. */
  donations: Map<string, number>;
  totalDiamonds: number;
  shieldUntil: number;
  /** Накопитель темпа впуска из очереди. */
  entryCredit: number;
  kills: number;
  losses: number;
}

/** Сквозная статистика участника — переживает смерть и выход из очереди. */
export interface Participant {
  userId: string;
  nickname: string;
  avatarUrl: string;
  team: TeamId | null;
  kills: number;
  deaths: number;
  bestLevel: number;
  diamonds: number;
  throneDamage: number;
}

/** Голосовые объявления диктора — серии убийств в духе Unreal Tournament. */
export type AnnounceId =
  | 'first_blood'
  | 'double'
  | 'multi'
  | 'mega'
  | 'ultra'
  | 'monster'
  | 'rampage'
  | 'dominating'
  | 'headshot';

export type MatchPhase = 'lobby' | 'battle' | 'finale' | 'results';
export type EndReason = 'throne' | 'timeout' | 'manual';

/** Визуальные события для рендера. Симуляция копит, рендер забирает. */
export type Fx =
  | { kind: 'banner'; title: string; subtitle: string; color: number; ms: number }
  | { kind: 'damage'; x: number; y: number; amount: number; big: boolean; color: number }
  | { kind: 'shock'; x: number; y: number; radius: number; color: number }
  | { kind: 'laser'; x1: number; y1: number; x2: number; y2: number; color: number; width: number }
  | { kind: 'levelup'; x: number; y: number; level: number; color: number }
  | {
      kind: 'cast';
      userId: string;
      avatarUrl: string;
      x: number;
      y: number;
      color: number;
      ms: number;
      /** Какой файл проиграть при пуске. */
      sfx: 'rocket' | 'laser' | 'none';
    }
  | { kind: 'death'; x: number; y: number; color: number }
  | { kind: 'volleywave'; x: number; y: number }
  | { kind: 'explosion'; x: number; y: number; radius: number }
  | { kind: 'screenshake'; power: number }
  | { kind: 'announce'; id: AnnounceId; nickname: string; color: number };

export interface MatchState {
  matchId: string;
  phase: MatchPhase;
  /** Часы симуляции в мс. Не Date.now(): замедление в финале, ускорение в дев-режиме. */
  now: number;
  startedAt: number;
  phaseEndsAt: number;

  units: Map<string, Unit>;
  /** Плоский массив тех же бойцов — для горячих циклов. */
  unitList: Unit[];
  projectiles: Projectile[];
  teams: Record<TeamId, TeamState>;
  thrones: Record<TeamId, Throne>;
  participants: Map<string, Participant>;

  /** Общий масштаб бойцов по плотности поля. */
  densityScale: number;

  /**
   * Y невидимой линии фронта. Бойцы не пересекают нейтральную полосу вокруг
   * неё, а сама линия ползёт в сторону той команды, которой меньше.
   */
  frontY: number;

  viewerCount: number;
  likeCounter: number;
  likeThreshold: number;

  winner: TeamId | null;
  endReason: EndReason | null;

  /** Счёт по выигранным раундам за весь эфир. */
  rounds: Record<TeamId, number>;

  stats: {
    /** Монотонный счётчик выстрелов — по его приросту звук делает залпы. */
    shotsFired: number;
    kills: number;
    entries: number;
    weaponsUsed: Record<WeaponId, number>;
    siegeSecondsA: number;
    siegeSecondsB: number;
  };

  fx: Fx[];
  ticker: { text: string; color: number; until: number }[];
}

export const other = (t: TeamId): TeamId => (t === 'A' ? 'B' : 'A');

export function createTeam(
  id: TeamId,
  label: string,
  color: number,
  colorDark: number,
): TeamState {
  return {
    id,
    label,
    color,
    colorDark,
    queue: [],
    donations: new Map(),
    totalDiamonds: 0,
    shieldUntil: 0,
    entryCredit: 0,
    kills: 0,
    losses: 0,
  };
}
